import { posthog } from 'posthog-js';
import type { AnalyticsEvent, AnalyticsProperties } from './events';
import { AnalyticsEvents } from './events';

/**
 * Records a named analytics event. Safe to call anywhere: PostHog drops the event when the
 * user has not allowed analytics (capturing is opted out by default), so callers never need
 * to check consent themselves.
 */
export const track = (event: AnalyticsEvent, properties?: AnalyticsProperties) => {
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never break the app.
  }
};

/** Groups an app path into the screen names used in analytics. IDs never leave this function. */
export const screenFromPath = (pathname: string): string => {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return 'home';
  if (p === '/jobs') return 'job_list';
  if (/^\/job\/[^/]+\/statistics$/.test(p)) return 'statistics';
  if (/^\/job\/[^/]+\/[^/]+$/.test(p)) return 'candidate_screening';
  if (/^\/job\/[^/]+$/.test(p)) return 'job_board';
  if (p.startsWith('/candidates')) return 'candidate_profile';
  if (p.startsWith('/application/share')) return 'shared_summary';
  if (p.startsWith('/outreach')) return 'outreach';
  if (/^\/(questions|templates|agent-chat)(\/|$)/.test(p)) return 'template_tools';
  if (p.startsWith('/settings')) return 'settings';
  if (/^\/(BhWidget|BhrcWidget|MsfWidget)$/.test(p)) return 'widget';
  if (p.startsWith('/signin')) return 'signin';
  return 'other';
};

const ERROR_DEDUPE_MS = 60_000;
const lastErrorAt = new Map<string, number>();

/**
 * Records that the recruiter was shown an error. Never pass the error message: it can contain
 * names or other data. The same error on the same screen is sent at most once per minute, so
 * an outage does not flood the analytics.
 */
export const trackError = (source: 'graphql' | 'api' | 'crash', status?: number) => {
  const screen = screenFromPath(window.location.pathname);
  const key = `${screen}|${source}|${status ?? ''}`;
  const now = Date.now();
  if (now - (lastErrorAt.get(key) ?? 0) < ERROR_DEDUPE_MS) return;
  lastErrorAt.set(key, now);
  track(AnalyticsEvents.ERROR_SHOWN, { screen, source, status });
};
